// calendarConstants.js — pt89U (Feb 20, 2026, Randy).
//
// Pure-data + pure-function helpers shared between CalendarSection.jsx
// and its extracted sub-components (EventEditorModal, MonthGrid, etc.).
// Nothing here touches state, localStorage, or the DOM — that stays in
// CalendarSection so the refactor is behaviourally identical.
//
// Values below match verbatim what lived inline in CalendarSection.jsx
// prior to the Feb-20 refactor pass. Do NOT change colors, labels, or
// option shapes without touching every consumer at the same time — the
// EventEditorModal + MonthGrid + DayList all read these off the object.

// Locale-agnostic weekday initials for the calendar column headers +
// day-of-week toggle pills. Sunday-first per North American convention.
export const DOW_MIN = ["S", "M", "T", "W", "T", "F", "S"];
export const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Kind metadata — one entry per calendar-event kind. Drives the pill
// color + dot color + short label in the pickers. Four kinds are the
// live set today (no "reward" — rewards live in FamilyRewards, not on
// the calendar grid). Stage-C will thread these labels through the
// theme system via useThemeLabel(); until then the .label field is
// the display source.
export const KIND_META = {
  event:   { label: "Event",   dot: "#4A5D4E", bg: "#EAF0EB" },
  task:    { label: "Task",    dot: "#C19A4A", bg: "#F8F1E2" },
  routine: { label: "Routine", dot: "#2C6E92", bg: "#E3EFF7" },
  ping:    { label: "Ping",    dot: "#C16E5A", bg: "#FBEBE4" },
};

// Reminder offset options — {v: minutes-before-start, label: display}.
// Field name is `label` (not `l`) — the extracted EventEditorModal
// reads this shape too.
export const REMINDER_OPTS = [
  { v: 0,    label: "At time" },
  { v: 10,   label: "10 min before" },
  { v: 30,   label: "30 min before" },
  { v: 60,   label: "1 hour before" },
  { v: 1440, label: "1 day before" },
];

// Format a "YYYY-MM-DD" ISO date in the LOCAL timezone (not UTC). The
// browser's built-in toISOString() converts to UTC first, which flips
// dates for anyone west of the UK on the previous evening — so we hand-
// format instead. Used everywhere the calendar renders a specific day.
export const isoLocal = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Inverse of isoLocal — parse "YYYY-MM-DD" into a Date at LOCAL midnight.
export const parseLocal = (s) => {
  const [y, m, dd] = s.split("-").map(Number);
  return new Date(y, m - 1, dd);
};

// Shift a Date by N whole days (positive or negative).
export const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

// Format "HH:MM" (24-hour) as a 12-hour string like "9:30 AM". Empty
// input returns empty string.
export const fmt12 = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ap}`;
};
